import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { updateCartName } from 'api/operations/cartServiceV2';
import { updateProposalName } from 'api/operations/enrollmentService';
import { updateGroup } from 'api/operations/groupService';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import { AltusQuoteProposalInfoFormDelegate } from './proposalFormDelegate';

// Currently supports only the GROUP rate segment.
// The CUSTOM rate segment has a dedicated page in the RFQ proposal initiation checklist.
export default class AltusQuoteProposalInfoPageStore {
  private readonly _domainParams: QuoteFlowDomainParams;
  private _isActionLoading = false;

  public flowDelegate: QuoteFlowDelegate;
  public proposalFormDelegate: AltusQuoteProposalInfoFormDelegate;

  public get proposalForm(): AltusQuoteProposalInfoFormDelegate['proposalForm'] {
    return this.proposalFormDelegate.proposalForm;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get canCompleteStep(): boolean {
    return (
      this.proposalForm.basicFieldsValidationsPassed &&
      !this.proposalForm.isLoading &&
      !this._isActionLoading &&
      !this.flowDelegate.isInitialDataLoading
    );
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public async getInitialData(): Promise<void> {
    const cartId = this._domainParams.cartId;
    const cartModule = this.flowDelegate.cartModule;

    this.flowDelegate.setIsInitialDataLoading(true);

    await this.flowDelegate.getFlowInitialData();
    await cartModule.getCart(cartId);

    runInAction(() => {
      const { effectiveDate, zipCode, state, county } = cartModule.lastUpdateBrowsingCriteria;

      this.proposalFormDelegate.proposalForm.populateFields({
        proposalName: cartModule.cart.cartName,
        effectiveDate,
        zip: zipCode,
        state,
        county,
      });

      this.flowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    const { cartId } = this._domainParams;

    this._setActionLoading(true);

    const inputGroup = buildInputGroupFromGroup(this.flowDelegate.groupModule.group);
    const { proposalName } = this.proposalForm.getFormDataObject() as Record<string, string>;

    try {
      if (this.flowDelegate.cartModule.cart.cartName !== proposalName) {
        await Promise.all([
          updateCartName(cartId, proposalName),
          updateProposalName(this._domainParams.periodId, cartId, proposalName),
          updateGroup(inputGroup),
        ]);
      }

      runInAction(() => {
        this._setActionLoading(false);
        this.flowDelegate.onCompleteRedirect();
      });
    } catch (error) {
      this._setActionLoading(false);
      toastManager.addToast('An error occurred while updating the proposal answers.', ToastColorTypes.WARNING);
    }
  }

  constructor(params: QuoteFlowDomainParams) {
    this._domainParams = params;

    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: params,
      nextPageId: QuoteRouteV2Id.QUOTE_CHECKLIST,
    });

    this.proposalFormDelegate = new AltusQuoteProposalInfoFormDelegate();

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const AltusQuoteProposalInfoPageStoreContext = createContext(
  new AltusQuoteProposalInfoPageStore({} as QuoteFlowDomainParams),
);

export const useAltusQuoteProposalInfoPageStore = (): AltusQuoteProposalInfoPageStore => {
  return useContext(AltusQuoteProposalInfoPageStoreContext);
};

export { AltusQuoteProposalInfoPageStore, AltusQuoteProposalInfoPageStoreContext };
